import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_DOMAIN } from 'consts';
import { makeApiRequest } from 'helpers';
import { security } from 'shared';

export type ChatShareAudience = 'any_authenticated' | 'email';

export type ChatShare = {
  id: number;
  chat_id: string;
  created_by_user_id: number;
  audience: ChatShareAudience;
  recipient_email: string | null;
  created_at: string;
  revoked_at: string | null;
};

type CreateShareResponse = ChatShare & { url: string; emailSent: boolean | null };

async function authedFetch (path: string, init: globalThis.RequestInit): Promise<Response> {
  const token = await security.getAccessTokenSilently()();
  return fetch(`${API_DOMAIN}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
}

/**
 * `enabled` gates the fetch: every sidebar row mounts a (closed) ShareDialog,
 * so an ungated query fired one request per listed chat on every page load.
 */
export function useChatShares (chatId: string | undefined, enabled = true) {
  return useQuery<{ results: ChatShare[] }>({
    queryKey: ['chat-shares', chatId],
    queryFn: () => makeApiRequest(`/api/chats/${chatId}/shares`),
    enabled: Boolean(chatId) && enabled,
  });
}

export function useCreateChatShare (chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { audience: ChatShareAudience; recipientEmail?: string }) => {
      const resp = await authedFetch(`/api/chats/${chatId}/shares`, {
        method: 'POST',
        body: JSON.stringify({ audience: params.audience, recipientEmail: params.recipientEmail }),
      });
      if (!resp.ok) throw new Error(`Failed to create share: ${resp.status}`);
      return resp.json() as Promise<CreateShareResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-shares', chatId] });
    },
  });
}

export function useRevokeChatShare (chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shareId: number) => {
      const resp = await authedFetch(`/api/chats/${chatId}/shares/${shareId}`, {
        method: 'DELETE',
        headers: {},
      });
      if (!resp.ok) throw new Error(`Failed to revoke share: ${resp.status}`);
      return resp.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-shares', chatId] });
    },
  });
}
